import { ChoiceNode as ChoiceSchema } from '@seen/core/tiptap/schema'
import { generateUUID } from '@seen/core/utils'
import { generateHTML, generateJSON, type JSONContent } from '@tiptap/core'
import { type NodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { JSX } from 'react'
import { ActionableChoice } from '#src/components/rich-text-editor/choice/ActionableChoice.tsx'
import { EditableChoice } from '#src/components/rich-text-editor/choice/editable-choice/EditableChoice.tsx'
import { getRenderExtensions } from '#src/components/rich-text-editor/extensions.ts'

export const ChoiceNode = ChoiceSchema.extend<{
  editable: boolean
}>({
  addNodeView() {
    return ReactNodeViewRenderer(ChoiceNodeComponent)
  },
})

function ChoiceNodeComponent(props: NodeViewProps): JSX.Element {
  const extension = props.extension as typeof ChoiceNode
  const editable = extension.options.editable
  return editable ? (
    <EditableChoice {...props} />
  ) : (
    <ActionableChoice {...props} />
  )
}

/**
 * Assigns a fresh opaque `fieldId` (UUID) to every `choice` node in the given
 * editor HTML that does not already have one, and returns the re-serialized
 * HTML. Idempotent: nodes with an existing `fieldId` are left untouched, so a
 * fresh UUID is created only per truly id-less node (never reused — important
 * for copy-paste safety).
 *
 * String-in / string-out so it can run both on the template-save path
 * (TemplatePage) and on the wave-3 one-time backfill over stored template HTML,
 * neither of which has a live editor instance. Round-trips through the shared
 * render schema (getRenderExtensions) so every node type — and every data-*
 * attribute — survives the parse → create → serialize cycle.
 * @param html - The editor/template HTML to scan for id-less choice nodes.
 * @returns The re-serialized HTML with every choice node carrying a fieldId.
 */
export function createChoiceFieldIds(html: string): string {
  /* getRenderExtensions is imported from extensions.ts, which in turn imports
   * ChoiceNode from this file — a module cycle. Calling it lazily here (at
   * call time, not module-init) means both modules are fully resolved by the
   * time the binding is read, so the cycle is safe. */
  const extensions = getRenderExtensions()
  const json = generateJSON(html, extensions) as JSONContent
  let mutated = false

  const visit = (node: JSONContent): void => {
    if (node.type === 'choice' && !node.attrs?.fieldId) {
      node.attrs = { ...node.attrs, fieldId: generateUUID() }
      mutated = true
    }
    node.content?.forEach(visit)
  }
  visit(json)

  // Avoid a needless re-serialization (and its whitespace/attr churn in the
  // saved body) when there was nothing to create.
  return mutated ? generateHTML(json, extensions) : html
}
